/**
 * BankLoan (BS-CL-BLS) MULTI 多年保存 / Tab 结转 helpers。
 *
 * Save 批量：
 * - 当前 Tab 年份直接使用最新保存 wire（detailsPayload）；
 * - 往后年：结转仍在期限内的贷款 + 回填 Bank/Loan ac no + 按目标年 period 重算 Summary sums；
 * - 目标年无 draft/cache 时仍写入结转行（不再丢成 details:{}）。
 *
 * Tab 打开（seamless）：从上一 YOA draft 结转到当前 Tab（同 Save 口径），供 BankLoan table 展示。
 */
import {parseYMD} from '@/utils/dataUtils';
import type {BankLoanRow} from '@/services/auditService/interface/wp-row';
import type {LoanScheduleItemVO} from '@/services/auditService/auditLoanCalculatorService';
import {
    getWpYoaMapCache,
    projectYoaBucketToWorkingPaperData,
} from '@/services/auditService/wpYoaMapCache';
import {
    computeBankLoanSumsFromSchedule,
    fetchBankLoanScheduleRows,
    mergeBankLoanSumsPreserveChanges,
    sanitizeBankLoanSumsForSave,
    applyBankLoanRepaymentSignForSave,
    type BankLoanSumPeriodWindow,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanSumCalc';
import type {BankLoanSums} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import {resolvePriorPeriodSlice} from '@/utils/wpPriorPeriodBfRollForward';

export type BankLoanYoaPeriodSlice = {
    key?: string;
    periodFrom?: string | null;
    periodTo?: string | null;
    yearOfAssessment?: string | null;
};

/**
 * 贷款在目标年份是否仍在期限内：lastRepay ≥ 目标年 periodFrom 视为仍在还款。
 * 最后还款日早于目标年开始（含落在本年即已还清）→ 忽略，不传递。
 * 日期无法解析时保守传递（与「除最后期限外均传递」的默认口径一致）。
 */
export function bankLoanStillLiveForPeriod(
    loan: Pick<BankLoanRow, 'lastRepay' | 'earlyRepay'> | null | undefined,
    periodFrom?: string | null,
): boolean {
    const from = parseYMD(periodFrom);
    const last = parseYMD(loan?.lastRepay);
    const early = parseYMD(loan?.earlyRepay);
    // 提早还款日优先于合同最后还款日：目标年 periodFrom 晚于截止日 → 不再生成该年记录
    const end = (early && last)
        ? (early.getTime() < last.getTime() ? early : last)
        : (early || last);
    if (!end || !from) return true;
    return end.getTime() >= from.getTime();
}

/** 兼容 {bankLoans:[...]} / [{bankLoans:[...]}] / bucket.details 等形态（同 BankLoan.bankLoans 提取口径）。 */
export function extractBankLoans(detail: any): BankLoanRow[] {
    if (!detail) return [];
    if (!Array.isArray(detail) && typeof detail === 'object') {
        if (Array.isArray(detail.bankLoans)) return detail.bankLoans as BankLoanRow[];
        if (detail.details && typeof detail.details === 'object') {
            return extractBankLoans(detail.details);
        }
        return [];
    }
    if (Array.isArray(detail) && detail.length > 0 && detail[0] && typeof detail[0] === 'object') {
        if (Array.isArray(detail[0].bankLoans)) return detail[0].bankLoans as BankLoanRow[];
    }
    return [];
}

function hasBankLoansArray(detail: any): boolean {
    return !!(detail && typeof detail === 'object' && !Array.isArray(detail) && Array.isArray(detail.bankLoans));
}

function normKey(v: unknown): string {
    return String(v ?? '').trim().toLowerCase();
}

function bankLoanIdentityKey(loan: any): string {
    const bank = normKey(loan?.bank);
    const loanNo = normKey(loan?.loanNo);
    return (bank || loanNo) ? `b:${bank}|n:${loanNo}` : '';
}

/** bank+loanNo 均为空时，用稳定的贷款要素（日期/金额/类型）去重。 */
function bankLoanFallbackKey(loan: any): string {
    return JSON.stringify([
        normKey(loan?.firstRepay),
        normKey(loan?.lastRepay),
        normKey(loan?.loanType),
        Number(loan?.loanAmt) || 0,
        Number(loan?.instalment) || 0,
    ]);
}

export function sameBankLoanIdentity(a: any, b: any): boolean {
    const keyA = bankLoanIdentityKey(a);
    const keyB = bankLoanIdentityKey(b);
    if (keyA && keyB) return keyA === keyB;
    // 任一侧缺 bank/loanNo（常见：下一年尚未填身份字段）→ 用贷款要素回退匹配
    return bankLoanFallbackKey(a) === bankLoanFallbackKey(b);
}

/** 结转时把本年 Bank / Loan ac no 写到目标年已有行（非空才覆盖）。 */
function mergeBankLoanIdentityFields(target: BankLoanRow, source: BankLoanRow): BankLoanRow {
    const bank = String(source?.bank ?? '').trim();
    const loanNo = String(source?.loanNo ?? '').trim();
    if (!bank && !loanNo) return target;
    return {
        ...target,
        ...(bank ? {bank: source.bank} : {}),
        ...(loanNo ? {loanNo: source.loanNo} : {}),
    };
}

function stripSumsRowIds(sums: any): any {
    if (!sums || typeof sums !== 'object') return sums;
    const {id: _id, bankLoanId: _bankLoanId, ...rest} = sums;
    return sanitizeBankLoanSumsForSave(rest);
}

/** Drop unset *Change / schedule overrides on every loan in a details bag (save wire). */
export function sanitizeBankLoansDetailsForSave(details: any): any {
    if (!details || typeof details !== 'object') return details;
    const loans = extractBankLoans(details);
    if (loans.length === 0 && !Array.isArray(details.bankLoans)) return details;
    return {
        ...details,
        bankLoans: loans.map((loan) => ({
            ...loan,
            // Repayment during the year (currentLastYoa*) → negative on wire
            sums: applyBankLoanRepaymentSignForSave(sanitizeBankLoanSumsForSave(loan.sums)),
        })),
    };
}

/** Prefer *Change when set, else base (Summary / WP resolve). */
function resolveSumField(
    sums: BankLoanRow['sums'] | null | undefined,
    base: 'currentYoa' | 'nonCurrentYoa' | 'currentLastYoa' | 'interestYoa',
    change: 'currentYoaChange' | 'nonCurrentYoaChange' | 'currentLastYoaChange' | 'interestYoaChange',
): number {
    if (!sums) return 0;
    const c = (sums as any)[change];
    if (c != null && c !== '') return Number(c) || 0;
    return Number((sums as any)[base]) || 0;
}

/** Prior YOA year-end outstanding → next Tab b/f (currentTotalInput). */
export function bankLoanPriorYearEndBalance(loan: BankLoanRow | null | undefined): number {
    if (!loan?.sums) return Number(loan?.currentTotalInput) || 0;
    return resolveSumField(loan.sums, 'currentYoa', 'currentYoaChange')
        + resolveSumField(loan.sums, 'nonCurrentYoa', 'nonCurrentYoaChange');
}

/**
 * MULTI later Tab Summary last-YOA column display:
 * values come from prior YOA current-column (xxxChange if not null, else calculated xxx).
 */
export type BankLoanLastYoaColumnDisplay = {
    interest: number;
    principalLt1: number;
    total: number;
};

export function resolveBankLoanLastYoaColumnFromPrior(
    prior: BankLoanRow | null | undefined,
): BankLoanLastYoaColumnDisplay | null {
    if (!prior) return null;
    return {
        interest: resolveSumField(prior.sums, 'interestYoa', 'interestYoaChange'),
        principalLt1: resolveSumField(prior.sums, 'currentYoa', 'currentYoaChange'),
        total: bankLoanPriorYearEndBalance(prior),
    };
}

/** Match prior-year loan for last-YOA Summary display (identity = BankLoan table roll). */
export function findPriorBankLoanForLastYoaDisplay(
    current: BankLoanRow | null | undefined,
    priorLoans: BankLoanRow[] | null | undefined,
): BankLoanRow | null {
    if (!current || !priorLoans?.length) return null;
    return priorLoans.find((loan) => sameBankLoanIdentity(current, loan)) ?? null;
}

/** 结转行 = 目标年份的新行（后端 insert，绝不跨年 update）。 */
export function toPropagatedBankLoanRow(loan: BankLoanRow): BankLoanRow {
    const {
        id: _id,
        lineId: _lineId,
        headerId: _headerId,
        sums,
        ...rest
    } = loan as BankLoanRow & {id?: number; lineId?: number; headerId?: string};
    return {
        ...rest,
        // next-year b/f ← prior year-end Current + Non-current
        currentTotalInput: bankLoanPriorYearEndBalance(loan),
        sums: stripSumsRowIds(sums),
    } as BankLoanRow;
}

/** MULTI Tab index → schedule/Summary date windows (current = this Tab; last = prior Tab). */
export function sumPeriodWindow(
    periods: BankLoanYoaPeriodSlice[],
    index: number,
): {
    periodFrom?: string | null;
    periodTo?: string | null;
    lastYearPeriodFrom?: string | null;
    lastYearPeriodTo?: string | null;
} {
    const period = periods[index];
    const prior = index > 0 ? periods[index - 1] : null;
    return {
        periodFrom: period?.periodFrom,
        periodTo: period?.periodTo,
        lastYearPeriodFrom: prior?.periodFrom ?? null,
        lastYearPeriodTo: prior?.periodTo ?? null,
    };
}

async function resolveScheduleForLoan(
    loan: BankLoanRow,
    cache: Map<string, LoanScheduleItemVO[] | null>,
): Promise<LoanScheduleItemVO[] | null> {
    return fetchBankLoanScheduleRows(loan, cache);
}

function applyComputedSums(
    loan: BankLoanRow,
    schedule: LoanScheduleItemVO[] | null,
    window: ReturnType<typeof sumPeriodWindow>,
    preserveChanges: boolean,
): BankLoanRow {
    if (!schedule || schedule.length === 0) return loan;
    const computed = computeBankLoanSumsFromSchedule(schedule, window, loan.earlyRepay);
    if (preserveChanges) {
        return {
            ...loan,
            sums: mergeBankLoanSumsPreserveChanges(loan.sums, computed),
        };
    }
    return {
        ...loan,
        sums: stripSumsRowIds(computed),
    };
}

/** Fingerprint for roll / column sync (Bank, Loan ac no, Current, Non-current). */
export function bankLoansDisplayFingerprint(loans: BankLoanRow[] | null | undefined): string {
    return (loans || []).map((l) => [
        bankLoanIdentityKey(l) || bankLoanFallbackKey(l),
        String(l?.bank ?? ''),
        String(l?.loanNo ?? ''),
        resolveSumField(l?.sums, 'currentYoa', 'currentYoaChange'),
        resolveSumField(l?.sums, 'nonCurrentYoa', 'nonCurrentYoaChange'),
    ].join(':')).join('|');
}

async function mergeForwardLoans(args: {
    existingLoans: BankLoanRow[];
    sourceLoans: BankLoanRow[];
    periodFrom?: string | null;
    window: ReturnType<typeof sumPeriodWindow>;
    scheduleCache: Map<string, LoanScheduleItemVO[] | null>;
}): Promise<{loans: BankLoanRow[]; changed: boolean}> {
    const liveSource = (args.sourceLoans || [])
        .filter((loan) => bankLoanStillLiveForPeriod(loan, args.periodFrom));
    if (liveSource.length === 0) {
        return {loans: args.existingLoans, changed: false};
    }

    let changed = false;
    const mergedExisting: BankLoanRow[] = [];
    for (const ex of args.existingLoans) {
        const source = liveSource.find((loan) => sameBankLoanIdentity(ex, loan));
        if (!source) {
            mergedExisting.push(ex);
            continue;
        }
        const withIdentity = mergeBankLoanIdentityFields(ex, source);
        const identityChanged = withIdentity.bank !== ex.bank || withIdentity.loanNo !== ex.loanNo;
        const bfEmpty = !(Number(withIdentity.currentTotalInput) > 0);
        const withBf = bfEmpty
            ? {...withIdentity, currentTotalInput: bankLoanPriorYearEndBalance(source)}
            : withIdentity;
        const schedule = await resolveScheduleForLoan(source, args.scheduleCache);
        const next = applyComputedSums(withBf, schedule, args.window, true);
        if (
            identityChanged
            || bfEmpty
            || bankLoansDisplayFingerprint([next]) !== bankLoansDisplayFingerprint([ex])
        ) {
            changed = true;
        }
        mergedExisting.push(next);
    }

    const additions: BankLoanRow[] = [];
    for (const loan of liveSource) {
        if (mergedExisting.some((ex) => sameBankLoanIdentity(ex, loan))) continue;
        const propagated = toPropagatedBankLoanRow(loan);
        const schedule = await resolveScheduleForLoan(loan, args.scheduleCache);
        additions.push(applyComputedSums(propagated, schedule, args.window, false));
        changed = true;
    }

    return {loans: [...mergedExisting, ...additions], changed};
}

/**
 * Recompute bank loan Summary sums for a Tab period window (schedule cut by earlyRepay).
 * MULTI first Tab has no prior-roll path — call this so Repayment / Current / Non-current
 * match RepaymentScheduleDrawer YOA Principal for that Tab.
 * clearStaleZeroPrincipalCurrChange: drop currentLastYoaChange===0 (historic empty→0) so table
 * Repayment follows recomputed currentLastYoa; non-zero manual overrides stay.
 */
export async function refreshBankLoanSumsForWindow(args: {
    loans: BankLoanRow[];
    window: BankLoanSumPeriodWindow;
    clearStaleZeroPrincipalCurrChange?: boolean;
}): Promise<{loans: BankLoanRow[]; changed: boolean}> {
    const loans = args.loans || [];
    if (loans.length === 0) {
        return {loans, changed: false};
    }
    const scheduleCache = new Map<string, LoanScheduleItemVO[] | null>();
    let changed = false;
    const next: BankLoanRow[] = [];
    for (const loan of loans) {
        const schedule = await resolveScheduleForLoan(loan, scheduleCache);
        if (!schedule || schedule.length === 0) {
            next.push(loan);
            continue;
        }
        const computed = computeBankLoanSumsFromSchedule(schedule, args.window, loan.earlyRepay);
        let sums = mergeBankLoanSumsPreserveChanges(loan.sums, computed) as BankLoanSums;
        const staleZeroChange = args.clearStaleZeroPrincipalCurrChange
            && sums.currentLastYoaChange != null
            && Number(sums.currentLastYoaChange) === 0;
        if (staleZeroChange) {
            // Historic empty→0 only — not a Drawer update; schedule must not touch real keyboard *Change.
            const cleared = sanitizeBankLoanSumsForSave({
                ...sums,
                currentLastYoaChange: null,
            }) as BankLoanSums;
            if (sums.balanceLastYoa != null) {
                cleared.balanceLastYoa = sums.balanceLastYoa;
            }
            sums = cleared;
        }
        const before = JSON.stringify(sanitizeBankLoanSumsForSave(loan.sums));
        const after = JSON.stringify(sanitizeBankLoanSumsForSave(sums));
        if (before !== after) {
            changed = true;
        }
        next.push({...loan, sums});
    }
    return {loans: next, changed};
}

/**
 * MULTI Tab：从上一 YOA draft 结转到当前片（Bank / Loan ac no + Current / Non-current sums）。
 * 无变更返回 null。
 */
export async function applyBankLoanForwardRollFromPriors(
    currentData: any,
    periods: BankLoanYoaPeriodSlice[],
    activeSliceKey: string | null | undefined,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): Promise<any | null> {
    const prior = resolvePriorPeriodSlice(
        periods as Array<{key: string; periodFrom?: string; periodTo?: string; yearOfAssessment?: string | null}>,
        activeSliceKey,
    );
    if (!prior) return null;

    const index = periods.findIndex((p) => p.key && p.key === activeSliceKey);
    if (index <= 0) return null;

    const priorDetail = getDetail(`${apiCode}__${prior.priorKey}`);
    const priorLoans = extractBankLoans(priorDetail);
    if (priorLoans.length === 0) return null;

    const existingLoans = extractBankLoans(currentData);
    const window = sumPeriodWindow(periods, index);
    const scheduleCache = new Map<string, LoanScheduleItemVO[] | null>();
    const {loans, changed} = await mergeForwardLoans({
        existingLoans,
        sourceLoans: priorLoans,
        periodFrom: periods[index]?.periodFrom,
        window,
        scheduleCache,
    });
    if (!changed) return null;

    const base = (currentData && typeof currentData === 'object' && !Array.isArray(currentData))
        ? currentData
        : {};
    return {...base, bankLoans: loans};
}

export function bankLoanAllPriorOriginsFingerprint(
    periods: BankLoanYoaPeriodSlice[],
    activeSliceKey: string | null | undefined,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): string {
    const prior = resolvePriorPeriodSlice(
        periods as Array<{key: string; periodFrom?: string; periodTo?: string; yearOfAssessment?: string | null}>,
        activeSliceKey,
    );
    if (!prior) return '';
    return bankLoansDisplayFingerprint(extractBankLoans(getDetail(`${apiCode}__${prior.priorKey}`)));
}

/**
 * 构建多年批量保存 payload：`{ [yoa]: { details }, issueMark? }`。
 */
export async function buildBankLoanMultiYearBatchPayload(args: {
    periods: BankLoanYoaPeriodSlice[];
    apiCode: string;
    companyId: number;
    annualId: number;
    activeYoa?: string | null;
    activeSliceKey?: string | null;
    activeDetails?: Record<string, unknown> | null;
    getDetail: (draftKey: string) => any;
    issueMark?: {marked: boolean; remark?: string} | null;
}): Promise<Record<string, unknown> | null> {
    const periods = (args.periods || []).filter((p) => String(p?.yearOfAssessment || '').trim());
    if (periods.length < 2) return null;

    const activeYoa = String(args.activeYoa || '').trim();
    const activeSliceKey = String(args.activeSliceKey || '').trim();
    const cache = (args.companyId > 0 && args.annualId > 0)
        ? getWpYoaMapCache(args.companyId, args.annualId, args.apiCode)
        : null;

    let activeIndex = activeSliceKey
        ? periods.findIndex((p) => p.key && p.key === activeSliceKey)
        : -1;
    if (activeIndex < 0) {
        activeIndex = periods.findIndex((p) => String(p.yearOfAssessment || '').trim() === activeYoa);
    }

    const activeLoans = args.activeDetails ? extractBankLoans(args.activeDetails) : [];
    const payload: Record<string, unknown> = {};
    const scheduleCache = new Map<string, LoanScheduleItemVO[] | null>();

    for (let index = 0; index < periods.length; index++) {
        const period = periods[index];
        const yoa = String(period.yearOfAssessment || '').trim();
        if (!yoa) continue;

        if (activeYoa && yoa === activeYoa && args.activeDetails) {
            payload[yoa] = {details: sanitizeBankLoansDetailsForSave(args.activeDetails)};
            continue;
        }

        const sliceKey = String(period.key || '').trim();
        const draft = sliceKey ? args.getDetail(`${args.apiCode}__${sliceKey}`) : null;
        let detail: any = (draft && typeof draft === 'object' && !Array.isArray(draft)) ? draft : null;
        if (!detail && cache && cache.byYearOfAssessment[yoa]) {
            detail = projectYoaBucketToWorkingPaperData(cache, yoa);
        }
        const existingLoans = extractBankLoans(detail);

        const forward = activeIndex >= 0 && index > activeIndex;
        const liveActive = (forward && activeLoans.length > 0)
            ? activeLoans.filter((loan) => bankLoanStillLiveForPeriod(loan, period.periodFrom))
            : [];
        const window = sumPeriodWindow(periods, index);

        // 无 draft/cache：仍结转 live 贷款，避免后年 Tab 空白
        if (!detail) {
            if (liveActive.length === 0) {
                payload[yoa] = {details: {}};
                continue;
            }
            const {loans} = await mergeForwardLoans({
                existingLoans: [],
                sourceLoans: liveActive,
                periodFrom: period.periodFrom,
                window,
                scheduleCache,
            });
            payload[yoa] = {details: sanitizeBankLoansDetailsForSave({bankLoans: loans})};
            continue;
        }

        const {loans, changed} = await mergeForwardLoans({
            existingLoans,
            sourceLoans: liveActive,
            periodFrom: period.periodFrom,
            window,
            scheduleCache,
        });

        if (!hasBankLoansArray(detail) && !changed && loans.length === 0) {
            payload[yoa] = {details: {}};
            continue;
        }

        payload[yoa] = {details: sanitizeBankLoansDetailsForSave({bankLoans: loans})};
    }

    if (args.issueMark) {
        payload.issueMark = args.issueMark;
    }

    const yoaKeys = periods.map((p) => String(p.yearOfAssessment || '').trim()).filter(Boolean);
    const missing = yoaKeys.filter((y) => !payload[y]);
    if (missing.length > 0) return null;
    return payload;
}

/** Remove loans matching `targetLoan` identity from a list. */
export function removeMatchingBankLoans(
    loans: BankLoanRow[],
    targetLoan: BankLoanRow | null | undefined,
): BankLoanRow[] {
    if (!targetLoan) return loans || [];
    return (loans || []).filter((loan) => !sameBankLoanIdentity(loan, targetLoan));
}

/**
 * MULTI delete：从所有 YOA 的 bankLoans 去掉同一笔贷款，供 batchSaveOrUpdate。
 * - active YOA 用 activeDetails（调用方已先做本地移除）；
 * - 其余年 draft → YOA Map 缓存；无数据的年份 details:{} 不触碰。
 */
export function buildBankLoanMultiYearDeletePayload(args: {
    periods: BankLoanYoaPeriodSlice[];
    apiCode: string;
    companyId: number;
    annualId: number;
    targetLoan: BankLoanRow;
    activeYoa?: string | null;
    activeSliceKey?: string | null;
    activeDetails?: Record<string, unknown> | null;
    getDetail: (draftKey: string) => any;
    issueMark?: {marked: boolean; remark?: string} | null;
}): Record<string, unknown> | null {
    const periods = (args.periods || []).filter((p) => String(p?.yearOfAssessment || '').trim());
    if (periods.length < 2) return null;

    const activeYoa = String(args.activeYoa || '').trim();
    const activeSliceKey = String(args.activeSliceKey || '').trim();
    const cache = (args.companyId > 0 && args.annualId > 0)
        ? getWpYoaMapCache(args.companyId, args.annualId, args.apiCode)
        : null;

    const payload: Record<string, unknown> = {};

    for (const period of periods) {
        const yoa = String(period.yearOfAssessment || '').trim();
        if (!yoa) continue;

        if (activeYoa && yoa === activeYoa && args.activeDetails) {
            payload[yoa] = {details: sanitizeBankLoansDetailsForSave(args.activeDetails)};
            continue;
        }

        const sliceKey = String(period.key || '').trim();
        const draft = sliceKey ? args.getDetail(`${args.apiCode}__${sliceKey}`) : null;
        let detail: any = (draft && typeof draft === 'object' && !Array.isArray(draft)) ? draft : null;
        if (!detail && cache && cache.byYearOfAssessment[yoa]) {
            detail = projectYoaBucketToWorkingPaperData(cache, yoa);
        }

        if (!detail) {
            payload[yoa] = {details: {}};
            continue;
        }

        const existing = extractBankLoans(detail);
        const filtered = removeMatchingBankLoans(existing, args.targetLoan);
        if (!hasBankLoansArray(detail) && filtered.length === 0) {
            payload[yoa] = {details: {}};
            continue;
        }

        const base = (detail && typeof detail === 'object' && !Array.isArray(detail)) ? detail : {};
        payload[yoa] = {
            details: sanitizeBankLoansDetailsForSave({
                ...base,
                bankLoans: filtered,
            }),
        };
    }

    if (args.issueMark) {
        payload.issueMark = args.issueMark;
    }

    const yoaKeys = periods.map((p) => String(p.yearOfAssessment || '').trim()).filter(Boolean);
    const missing = yoaKeys.filter((y) => !payload[y]);
    if (missing.length > 0) return null;
    return payload;
}

/** BS loan codes that use MULTI YOA Map wire (BankLoan / VehicleLoan WP). */
const LOAN_BS_MULTI_ADJ_CODES = new Set([
    'BS-CL-BLS',
    'BS-NCL-BLS',
    'BS-CL-OFL',
    'BS-NCL-OFL',
]);

const YOA_KEY_RE = /^\d{4}\/\d{2}$/;

function isLoanBsMultiAdjCode(code: string): boolean {
    return LOAN_BS_MULTI_ADJ_CODES.has(String(code || '').trim().toUpperCase());
}

function isYoaAdjDetailsMap(detail: unknown): detail is Record<string, {details?: unknown}> {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const keys = Object.keys(detail as Record<string, unknown>);
    return keys.length > 0 && keys.every((k) => YOA_KEY_RE.test(k));
}

function stripLoanRowAdjFields(loan: Record<string, unknown>): Record<string, unknown> {
    const {adjustments: _adjList, adjustment: _adj, ...rest} = loan;
    return rest;
}

function extractVehicleLoans(detail: any): any[] {
    if (!detail) return [];
    if (!Array.isArray(detail) && typeof detail === 'object') {
        if (Array.isArray(detail.vehicleLoans)) return detail.vehicleLoans;
        if (detail.details && typeof detail.details === 'object') {
            return extractVehicleLoans(detail.details);
        }
    }
    return [];
}

/**
 * MULTI BankLoan/VehicleLoan WP→WP submit：details[0] = YOA Map
 * `{ [yoa]: { details: { bankLoans|vehicleLoans, adjustment? } } }`
 * 仅选定 YOA 桶挂 top-level `adjustment`（识别年份）；其它年保留贷款行无 adj。
 */
export function buildLoanMultiYearAdjDetailsMap(args: {
    code: string;
    companyId: number;
    annualId: number;
    selectedYoa: string;
    signedAdjustment: number;
    flatDetail: Record<string, any> | null | undefined;
    yoaOrder?: string[] | null;
}): Record<string, {details: Record<string, any>}> | null {
    const code = String(args.code || '').trim().toUpperCase();
    if (!isLoanBsMultiAdjCode(code)) return null;

    const selectedYoa = String(args.selectedYoa || '').trim();
    if (!selectedYoa || !YOA_KEY_RE.test(selectedYoa)) return null;

    const flat = args.flatDetail;
    if (flat && isYoaAdjDetailsMap(flat)) {
        return flat as Record<string, {details: Record<string, any>}>;
    }

    const loanKey = (code === 'BS-CL-OFL' || code === 'BS-NCL-OFL') ? 'vehicleLoans' : 'bankLoans';
    const cache = (args.companyId > 0 && args.annualId > 0)
        ? getWpYoaMapCache(args.companyId, args.annualId, code)
        : null;

    const fromArgs = (args.yoaOrder || [])
        .map((y) => String(y || '').trim())
        .filter((y) => YOA_KEY_RE.test(y));
    const fromCache = (cache?.yoaOrder || [])
        .map((y) => String(y || '').trim())
        .filter((y) => YOA_KEY_RE.test(y));
    const order = [...(fromArgs.length > 0 ? fromArgs : fromCache)];
    if (!order.includes(selectedYoa)) order.push(selectedYoa);
    if (order.length === 0) order.push(selectedYoa);

    const map: Record<string, {details: Record<string, any>}> = {};
    for (const yoa of order) {
        let loans: any[] = [];
        if (yoa === selectedYoa && flat) {
            loans = loanKey === 'bankLoans' ? extractBankLoans(flat) : extractVehicleLoans(flat);
        } else if (cache?.byYearOfAssessment?.[yoa]) {
            const projected = projectYoaBucketToWorkingPaperData(cache, yoa);
            loans = loanKey === 'bankLoans'
                ? extractBankLoans(projected)
                : extractVehicleLoans(projected);
        }

        const cleaned = (loans || [])
            .filter((row) => row && typeof row === 'object')
            .map((row) => stripLoanRowAdjFields(row as Record<string, unknown>));

        let detailsBag: Record<string, any> = loanKey === 'bankLoans'
            ? sanitizeBankLoansDetailsForSave({bankLoans: cleaned})
            : {vehicleLoans: cleaned};

        if (yoa === selectedYoa) {
            const signed = Number(args.signedAdjustment);
            detailsBag = {
                ...detailsBag,
                adjustment: Number.isFinite(signed) ? signed : 0,
            };
        }

        map[yoa] = {details: detailsBag};
    }
    return map;
}

export function isLoanMultiYearAdjDetailsMap(detail: unknown): boolean {
    return isYoaAdjDetailsMap(detail);
}
